import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import { AuthContext } from '@/contexts/AuthContext';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import styles from './Profile.module.css';
import {
	CustomDocumentsResourceService,
	CustomSignallerResourceService,
	DocumentsDTO,
	SignallerExtendedDTO
} from '@/openapi';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';
import { useRemoteGet } from '@/hooks/useRemoteGet';
import { useRemoteUpdate } from '@/hooks/useRemoteUpdate';
import { useTranslation } from 'react-i18next';
import Loader from '@/components/UI/Loader';
import Button from '@/components/UI/Button';
import PrivacyPolicyCard from '@/components/PrivacyPolicyCard';
import Modal from '@/components/UI/Modal';
import { useNavigate } from 'react-router-dom';
import RewardCard from '@/components/RewardCard';
import NotificationList from '@/components/NotificationList';
import { useRemotePost } from '@/hooks/useRemotePost';
import { useRemoteDelete } from '@/hooks/useRemoteDelete';
import { fileToBase64 } from '@/utils/utils';
import DocumentViewer from '@/components/DocumentViewer';
import { ReactComponent as Eye } from '@Assets/svg/eye.svg';
import { ReactComponent as Trash } from '@Assets/svg/trash.svg';
import SuccessToast from '@/components/UI/SuccessToast';

const Profile = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const authCtx = useContext(AuthContext);

	// Stato di editing
	const [editing, setEditing] = useState<boolean>(false);
	const [deleteAccountPopup, setDeleteAccountPopup] = useState<boolean>(false);

	// Stati per i toast di errore
	const [showToast, setShowToast] = useState<boolean>(false);
	const [toastMessage, setToastMessage] = useState<string>('');
	const [toastType, setToastType] = useState<'success' | 'error'>('error');

	// Stati dei campi del form
	const [username, setUsername] = useState<string>('');
	const [email, setEmail] = useState<string>('');
	const [firstName, setFirstName] = useState<string>('');
	const [lastName, setLastName] = useState<string>('');
	const [phone, setPhone] = useState<string>('');
	const [iban, setIban] = useState<string>('');
	const [taxCode, setTaxCode] = useState<string>('');

	// Stato per memorizzare i dati originali prima della modifica
	const [originalData, setOriginalData] = useState<SignallerExtendedDTO | null>(null);

	// Modale Privacy e Documento
	const [showPrivacyModal, setShowPrivacyModal] = useState<boolean>(false);
	const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
	const [selectedDocument, setSelectedDocument] = useState<{
		type: 'front' | 'back';
		data: string;
		contentType: string;
	} | null>(null);

	// Stato per la visibilità del popup della password
	const [showPasswordPopup, setShowPasswordPopup] = useState<boolean>(false);

	// Stati per documenti d'identità
	const [sigDocument, setSigDocument] = useState<DocumentsDTO | null>(null);
	const [frontDoc, setFrontDoc] = useState<File | null>(null);
	const [backDoc, setBackDoc] = useState<File | null>(null);

	// Stato per PWA install
	const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
	const [showInstallButton, setShowInstallButton] = useState<boolean>(false);

	const getProfileCompleted = useRemoteGet<SignallerExtendedDTO>({
		remoteMethod: async () => {
			if (!authCtx.userId) {
				throw new Error('User ID not available');
			}

			return CustomSignallerResourceService.getSignallerExtended({
				signallerId: authCtx.userId
			});
		},
		lazy: true
	});

	const updateProfile = useRemoteUpdate<SignallerExtendedDTO, SignallerExtendedDTO>({
		remoteMethod: async (signallerId, payload) => {
			return CustomSignallerResourceService.updateSignallerProfile({
				signallerId: parseInt(signallerId.toString()),
				requestBody: payload
			});
		},
		lazy: true
	});

	const addDocument = useRemotePost<DocumentsDTO, { signallerId: number; requestBody: DocumentsDTO }>({
		remoteMethod: CustomDocumentsResourceService.createDocuments1,
		lazy: true
	});

	const updateDocument = useRemoteUpdate<DocumentsDTO, DocumentsDTO>({
		remoteMethod: async (signallerId, payload) => {
			return CustomDocumentsResourceService.updateDocuments1({
				signallerId: parseInt(signallerId.toString()),
				requestBody: payload
			});
		},
		lazy: true
	});

	const findDocument = useRemoteGet<DocumentsDTO>({
		remoteMethod: async () => {
			if (!authCtx.userId) {
				throw new Error('User ID not available');
			}
			return CustomDocumentsResourceService.findDocumentsBySignaller({
				signallerId: authCtx.userId
			});
		},
		lazy: true
	});

	const removeDocument = useRemoteDelete<void>({
		remoteMethod: async () => {
			if (!authCtx.userId) {
				throw new Error('User ID not available');
			}
			return CustomDocumentsResourceService.deleteDocumentsOfSignaller({
				signallerId: parseInt(authCtx.userId.toString())
			});
		},
		lazy: true
	});

	const deleteAccount = useRemoteDelete<void>({
		remoteMethod: async (signallerId) => {
			if (!authCtx.userId) {
				throw new Error('User ID not available');
			}
			return CustomSignallerResourceService.deleteMyAccount({
				signallerId: parseInt(signallerId.toString())
			});
		},
		lazy: true
	});

	// Funzione per mostrare toast di errore o successo
	const showToastMessage = (message: string, type: 'success' | 'error' = 'error') => {
		setToastMessage(message);
		setToastType(type);
		setShowToast(true);
	};

	const handleCloseToast = () => {
		setShowToast(false);
		setToastMessage('');
	};

	// PULSANTE AGGIUNTA COLLEGAMENTO DESKTOP/HOME----------------------------------------------
	// Gestione PWA install prompt
	useEffect(() => {
		const handleBeforeInstallPrompt = (e: Event) => {
			// Previene il prompt automatico del browser
			e.preventDefault();
			setDeferredPrompt(e);
			setShowInstallButton(true);
		};

		const handleAppInstalled = () => {
			// Nasconde il pulsante dopo l'installazione
			setShowInstallButton(false);
			setDeferredPrompt(null);
		};

		window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt); // Si attiva quando il browser rileva che l'app può essere installata
		window.addEventListener('appinstalled', handleAppInstalled);

		// Controlla se l'app è in modalità standalone (già installata) (solo per alcuni browser)
		if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
			setShowInstallButton(false);
		} else {
			// Controlla se siamo su iOS Safari (che non supporta beforeinstallprompt)
			const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
			if (isIOSSafari) {
				setShowInstallButton(true); // Mostra sempre il button su iOS per le istruzioni manuali
			}
		}

		return () => {
			window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
			window.removeEventListener('appinstalled', handleAppInstalled);
		};
	}, []);

	// Gestisce l'installazione dell'app
	const handleInstallApp = async () => {
		if (!deferredPrompt) {
			// Fallback per quando non abbiamo il prompt nativo
			const userAgent = navigator.userAgent.toLowerCase();

			if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
				showToastMessage(t('profile.installApp.iosInstructions'), 'success');
			} else if (userAgent.includes('android')) {
				showToastMessage(t('profile.installApp.androidInstructions'), 'success');
			} else if (userAgent.includes('chrome')) {
				showToastMessage(t('profile.installApp.chromeInstructions'), 'success');
			} else if (userAgent.includes('firefox')) {
				showToastMessage(t('profile.installApp.firefoxInstructions'), 'success');
			} else {
				showToastMessage(t('profile.installApp.manualInstructions'), 'success');
			}
			return; // IMPORTANTE: esci qui per evitare l'errore
		}

		try {
			const result = await deferredPrompt.prompt();

			if (result.outcome === 'accepted') {
				showToastMessage(t('profile.installApp.success'), 'success');
			}

			setDeferredPrompt(null);
			setShowInstallButton(false);
		} catch (error) {
			console.error("Errore durante l'installazione:", error);
			showToastMessage(t('profile.installApp.error'));
		}
	};

	// MODIFICA E SALVATAGGIO DATI -------------------------------------------------------------
	const handleEditClick = () => {
		if (editing) {
			// Se siamo già in modalità modifica, annulla le modifiche per il ripristino dei campi
			if (originalData) {
				setUsername(originalData.login || '');
				setEmail(originalData.email || '');
				setFirstName(originalData.firstName || '');
				setLastName(originalData.lastName || '');
				setPhone(originalData.phone || '');
				setIban(originalData.iban || '');
				setTaxCode(originalData.taxCode || '');
			}

			// Reset dei file temporanei e ripristino documenti originali
			setFrontDoc(null);
			setBackDoc(null);

			// Ripristina i documenti originali ricaricandoli dal server
			if (authCtx.userId) {
				findDocument.fetch();
			}

			// Reset degli errori
			setShowToast(false);
			setToastMessage('');

			setEditing(false);
		} else {
			setEditing(true);
		}
	};

	// Gestisce il salvataggio del form
	const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setShowToast(false);
		setToastMessage('');

		// Valida i documenti di identità
		if (!validateIdDocuments()) {
			return;
		}

		if (!authCtx.userId || !originalData) {
			return;
		}

		// Prepara l'oggetto da inviare all'API
		const updatedProfile: SignallerExtendedDTO = {
			...originalData,
			signallerId: authCtx.userId,
			login: username,
			email: email,
			firstName: firstName,
			lastName: lastName,
			phone: phone.trim() === '' ? undefined : phone.trim(),
			iban: iban.trim() === '' ? undefined : iban.trim(),
			taxCode: taxCode.trim() === '' ? undefined : taxCode.trim()
		};

		// Invia l'aggiornamento al backend
		updateProfile.fetch(authCtx.userId, updatedProfile);
	};

	// ELIMINAZIONE ACCOUNT --------------------------------------------------------------------
	// Apre/Chiude il modale di conferma eliminazione account
	const toggleDeleteAccount = (e?: React.MouseEvent) => {
		if (e) {
			e.stopPropagation();
		}
		setDeleteAccountPopup(!deleteAccountPopup);
	};

	// Gestisce l'eliminazione quando l'utente conferma
	const handleConfirmDeleteAccount = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (authCtx.userId && deleteAccountPopup) {
			deleteAccount.fetch(authCtx.userId);
		}
		toggleDeleteAccount();
	};

	// CAMBIO PASSWORD -------------------------------------------------------------------------
	// Reindirizza l'utente alla pagina di cambio password
	const handleChangePasswordRedirect = () => {
		handleClosePasswordPopup();
		navigate('/reset/password/init');
	};

	const handlePasswordClick = () => {
		setShowPasswordPopup(true);
	};

	const handleClosePasswordPopup = () => {
		setShowPasswordPopup(false);
	};

	// UPLOAD DOCUMENTI ------------------------------------------------------------------------
	// Gestione caricamento documento identità
	const handleDocUpload = (side: 'front' | 'back') => (e: ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;

		const file = files[0];
		const supportedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

		if (supportedTypes.includes(file.type)) {
			side === 'front' ? setFrontDoc(file) : setBackDoc(file);
		} else {
			side === 'front' ? setFrontDoc(null) : setBackDoc(null);
			showToastMessage(t('profile.docError.formatNotSupported'));
		}
	};

	// Rimuove il documento caricato
	const handleRemoveDoc = (side: 'front' | 'back') => () => {
		if (side === 'front') {
			setFrontDoc(null);
			// Se il documento esisteva già, crea un marker per indicare la rimozione
			if (sigDocument?.frontDoc) {
				setSigDocument((prev) =>
					prev
						? {
								...prev,
								frontDoc: undefined,
								frontDocContentType: undefined,
								frontDocName: undefined
						  }
						: null
				);
			}
		} else {
			setBackDoc(null);
			if (sigDocument?.backDoc) {
				setSigDocument((prev) =>
					prev
						? {
								...prev,
								backDoc: undefined,
								backDocContentType: undefined,
								backDocName: undefined
						  }
						: null
				);
			}
		}
	};

	// Verifica la presenza di entrambi i documenti prima del salvataggio
	const validateIdDocuments = (): boolean => {
		if (!editing) return true;

		const finalFrontExists = frontDoc !== null || sigDocument?.frontDoc;
		const finalBackExists = backDoc !== null || sigDocument?.backDoc;

		// Se non ci sono documenti affatto, è valido
		if (!finalFrontExists && !finalBackExists) {
			return true;
		}

		// Se c'è almeno un documento, devono esserci entrambi
		if (!finalFrontExists || !finalBackExists) {
			showToastMessage(t('profile.docError.bothRequired'));
			return false;
		}

		return true;
	};

	// Salva le modifiche dei documenti al salvataggio dei dati del form
	const handleDocumentSubmit = async () => {
		if (!authCtx.userId) return;

		try {
			// Controllo se entrambi i documenti sono stati rimossi
			const finalFrontExists = frontDoc !== null || sigDocument?.frontDoc;
			const finalBackExists = backDoc !== null || sigDocument?.backDoc;

			// Se entrambi i documenti sono stati rimossi, elimina quelli esistenti
			if (!finalFrontExists && !finalBackExists && sigDocument?.id) {
				await removeDocument.fetch(sigDocument.id);
				return;
			}

			// Se manca uno dei due documenti non prosegue
			if (!finalFrontExists || !finalBackExists) {
				return;
			}

			// Prepara il nuovo oggetto DocumentsDTO
			let documentDTO: DocumentsDTO = {
				id: sigDocument?.id,
				dateInsert: sigDocument?.dateInsert || undefined
			};

			// Converte i file in Base64
			if (frontDoc) {
				const frontBase64 = await fileToBase64(frontDoc);
				documentDTO.frontDoc = frontBase64.replace(/^data:(.*);base64,/, '');
				documentDTO.frontDocContentType = frontDoc.type;
				documentDTO.frontDocName = frontDoc.name;
			} else if (sigDocument?.frontDoc) {
				// Mantieni il documento frontale esistente
				documentDTO.frontDoc = sigDocument.frontDoc;
				documentDTO.frontDocContentType = sigDocument.frontDocContentType;
				documentDTO.frontDocName = sigDocument.frontDocName;
			}

			if (backDoc) {
				const backBase64 = await fileToBase64(backDoc);
				documentDTO.backDoc = backBase64.replace(/^data:(.*);base64,/, '');
				documentDTO.backDocContentType = backDoc.type;
				documentDTO.backDocName = backDoc.name;
			} else if (sigDocument?.backDoc) {
				// Mantieni il documento posteriore esistente
				documentDTO.backDoc = sigDocument.backDoc;
				documentDTO.backDocContentType = sigDocument.backDocContentType;
				documentDTO.backDocName = sigDocument.backDocName;
			}

			// Invia i documenti al server in creazione o modifica
			if (sigDocument?.id) updateDocument.fetch(authCtx.userId, documentDTO);
			else {
				addDocument.fetch({
					signallerId: authCtx.userId,
					requestBody: documentDTO
				});
			}
		} catch (error) {
			console.error('Errore durante il caricamento dei documenti:', error);
			showToastMessage(t('profile.docError.uploadFailed'));
		}
	};

	const handleViewDocument = (type: 'front' | 'back') => {
		if (type === 'front') {
			const data = frontDoc ? URL.createObjectURL(frontDoc) : sigDocument?.frontDoc;
			const contentType = frontDoc ? frontDoc.type : sigDocument?.frontDocContentType;
			if (data && contentType) {
				setSelectedDocument({ type, data, contentType });
				setShowDocumentModal(true);
			}
		} else {
			const data = backDoc ? URL.createObjectURL(backDoc) : sigDocument?.backDoc;
			const contentType = backDoc ? backDoc.type : sigDocument?.backDocContentType;
			if (data && contentType) {
				setSelectedDocument({ type, data, contentType });
				setShowDocumentModal(true);
			}
		}
	};

	const handleCloseDocumentModal = () => {
		setShowDocumentModal(false);
		setSelectedDocument(null);
	};

	// EFFETTI ---------------------------------------------------------------------------------
	useEffect(() => {
		if (authCtx.userId) {
			getProfileCompleted.fetch();
			findDocument.fetch();
		}
		// Re-indirizzamento alla sezione notifiche (click dalla navbar)
		if (window.location.hash === '#notifications') {
			const notificationsElement = document.getElementById('notifications-section');
			if (notificationsElement) {
				notificationsElement.scrollIntoView({ behavior: 'smooth' });
			}
		}
		// Re-indirizzamento alla sezione premi (click dalla home)
		if (window.location.hash === '#rewards') {
			const notificationsElement = document.getElementById('rewards-section');
			if (notificationsElement) {
				notificationsElement.scrollIntoView({ behavior: 'smooth' });
			}
		}
	}, [authCtx.userId]);

	// Carica i dati del segnalatore
	useEffect(() => {
		if (getProfileCompleted.data) {
			const data = getProfileCompleted.data;
			setOriginalData(data);
			setUsername(data.login || '');
			setEmail(data.email || '');
			setFirstName(data.firstName || '');
			setLastName(data.lastName || '');
			setPhone(data.phone || '');
			setIban(data.iban || '');
			setTaxCode(data.taxCode || '');
		}
	}, [getProfileCompleted.data]);

	useEffect(() => {
		if (typeof updateProfile.error !== 'undefined') {
			let errorMessage = '';
			if (updateProfile.errorDetail === 'error.userexists' || updateProfile.errorDetail === 'Login name already used!') {
				errorMessage = t('signup.errUserAlreadyUsed');
			} else if (
				updateProfile.errorDetail === 'error.emailexists' ||
				updateProfile.errorDetail === 'Email is already in use!'
			) {
				errorMessage = t('signup.errMailAlreadyUsed');
			} else {
				errorMessage = t('profile.errGeneric');
			}
			showToastMessage(errorMessage);
		}
	}, [updateProfile.error]);

	useEffect(() => {
		if (updateProfile.data && !updateProfile.error) {
			const finalFrontExists = frontDoc !== null || (sigDocument?.frontDoc && sigDocument?.frontDoc !== null);
			const finalBackExists = backDoc !== null || (sigDocument?.backDoc && sigDocument?.backDoc !== null);
			const shouldUpdateDocuments = finalFrontExists || finalBackExists || sigDocument?.id;

			if (shouldUpdateDocuments) {
				handleDocumentSubmit();
			}

			// Aggiorna i dati del profilo e documenti
			getProfileCompleted.fetch();
			findDocument.fetch();
			setEditing(false);
			showToastMessage(t('profile.updateSuccess'), 'success');

			// Pulisci i file temporanei
			setFrontDoc(null);
			setBackDoc(null);
		}
	}, [updateProfile.data]);

	useEffect(() => {
		if (addDocument.data || updateDocument.data) {
			showToastMessage(t('profile.documents.uploadSuccess'), 'success');

			// Aggiorna la vista dei documenti
			findDocument.fetch();

			// Pulisce la UI
			setFrontDoc(null);
			setBackDoc(null);
		}
	}, [addDocument.data, updateDocument.data]);

	useEffect(() => {
		if (addDocument.error || updateDocument.error) {
			showToastMessage(t('profile.docError.uploadFailed'));
		}
	}, [addDocument.error, updateDocument.error]);

	useEffect(() => {
		if (findDocument.data) setSigDocument(findDocument.data);
		else setSigDocument(null);
	}, [findDocument.data, findDocument.error]);

	useEffect(() => {
		if (removeDocument.success) {
			setSigDocument(null);
			setFrontDoc(null);
			setBackDoc(null);

			// Ricarica i documenti
			findDocument.fetch();
		}
	}, [removeDocument.success]);

	useEffect(() => {
		if (deleteAccount.success && !deleteAccount.loading && !deleteAccount.error) {
			navigate('/');
			authCtx.logout();
		}
	}, [deleteAccount.success, deleteAccount.loading]);

	return (
		<div className={styles.mainContainer}>
			<Navbar className={styles.navDivider} />

			<main className={styles.mainContent}>
				<Loader
					visible={
						getProfileCompleted.loading ||
						updateProfile.loading ||
						addDocument.loading ||
						updateDocument.loading ||
						findDocument.loading ||
						removeDocument.loading ||
						deleteAccount.loading
					}
				/>

				<SuccessToast isVisible={showToast} message={toastMessage} type={toastType} onClose={handleCloseToast} />

				<section className={styles.heroSection}>
					<h1 className={styles.profileTitle}>
						{t('profile.welcome')} {firstName ? firstName : username}!
					</h1>
					{showInstallButton && (
						<div className={styles.install_app_container}>
							<p className={styles.install_app_text}>{t('profile.installApp.incitement')}</p>
							<Button
								onClick={handleInstallApp}
								typeSubmit="button"
								className={styles.installAppBtn}
								name={t('profile.installApp.button')}
								color="black"
								background="white"
							/>
						</div>
					)}
				</section>

				<div className={styles.gridContainer}>
					{/* Colonna sinistra SCALA PREMI */}
					<div className={styles.leftContainer} id="rewards-section">
						<RewardCard
							contractsCounter={getProfileCompleted.data?.contractsCurrentCycle || 0}
							totContractsCompleted={getProfileCompleted.data?.closedContracts || 0}
						/>
					</div>

					{/* Colonna centrale FORM DATI */}
					<div className={styles.formContainer}>
						<form onSubmit={handleSave} className={styles.form}>
							<div className={styles.field}>
								<label htmlFor="username">{t('profile.username')}</label>
								<input
									id="username"
									value={username}
									onChange={(e: ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
									disabled={!editing}
									className={editing ? styles.editable : ''}
								/>
							</div>
							<div className={styles.field}>
								<label htmlFor="password">{t('profile.password')}</label>
								<input
									id="password"
									type="password"
									value={'***************'}
									readOnly
									disabled={!editing}
									className={editing ? styles.editable : ''}
									onClick={handlePasswordClick}
								/>
							</div>
							<div className={styles.field}>
								<label htmlFor="email">{t('profile.mail')}</label>
								<input
									id="email"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									disabled={!editing}
									className={editing ? styles.editable : ''}
								/>
							</div>
							<div className={styles.field}>
								<label htmlFor="firstName">{t('profile.name')}</label>
								<input
									id="firstName"
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									disabled={!editing}
									className={editing ? styles.editable : ''}
								/>
							</div>
							<div className={styles.field}>
								<label htmlFor="lastName">{t('profile.surname')}</label>
								<input
									id="lastName"
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									disabled={!editing}
									className={editing ? styles.editable : ''}
								/>
							</div>
							<div className={styles.field}>
								<label htmlFor="phone">{t('profile.phone')}</label>
								<input
									id="phone"
									type="tel"
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									disabled={!editing}
									className={editing ? styles.editable : ''}
								/>
							</div>
							<div className={styles.field}>
								<label htmlFor="iban">{t('profile.iban')}</label>
								<input
									id="iban"
									value={iban}
									onChange={(e) => setIban(e.target.value)}
									disabled={!editing}
									className={editing ? styles.editable : ''}
								/>
							</div>
							<div className={styles.field}>
								<label htmlFor="taxCode">{t('profile.taxCode')}</label>
								<input
									id="taxCode"
									value={taxCode}
									onChange={(e) => setTaxCode(e.target.value)}
									disabled={!editing}
									className={editing ? styles.editable : ''}
								/>
							</div>

							{/* Sezione documenti d'identità */}
							<div className={styles.documentsSection}>
								<h3 className={styles.documentsTitle}>{t('profile.documents.title')}</h3>
								<p className={styles.documentsDescription}>{t('profile.documents.description')}</p>

								<div className={styles.documentsContainer}>
									{/* Documento fronte */}
									<div className={styles.documentItem}>
										<span className={styles.documentLabel}>{t('profile.documents.front')}</span>

										{frontDoc || sigDocument?.frontDoc ? (
											<div className={styles.documentPreview}>
												{editing ? (
													<button className={styles.viewIcon} onClick={handleRemoveDoc('front')} type="button">
														<Trash className={styles.trashIcon} />
													</button>
												) : (
													<button className={styles.viewIcon} onClick={() => handleViewDocument('front')} type="button">
														<Eye className={styles.eyeIcon} />
													</button>
												)}
												<div className={styles.previewIcon}>📄</div>
												<div className={styles.previewText}>
													{frontDoc ? frontDoc.name : sigDocument?.frontDocName || 'Fronte.file'}
												</div>
												<input
													type="file"
													id="frontIdUpload"
													onChange={handleDocUpload('front')}
													accept="application/pdf,image/jpeg,image/jpg,image/png"
													style={{ display: 'none' }}
													disabled={!editing}
												/>
											</div>
										) : (
											<div className={styles.uploadContainer}>
												<div className={styles.uploadIcon}>📁</div>
												<span className={styles.uploadText}>{t('profile.documents.uploadPdf')}</span>
												<input
													type="file"
													id="frontIdUpload"
													className={styles.uploadInput}
													onChange={handleDocUpload('front')}
													accept="application/pdf,image/jpeg,image/jpg,image/png"
													disabled={!editing}
												/>
											</div>
										)}
									</div>

									{/* Documento retro */}
									<div className={styles.documentItem}>
										<span className={styles.documentLabel}>{t('profile.documents.back')}</span>

										{backDoc || sigDocument?.backDoc ? (
											<div className={styles.documentPreview}>
												{editing ? (
													<button className={styles.viewIcon} onClick={handleRemoveDoc('back')} type="button">
														<Trash className={styles.trashIcon} />
													</button>
												) : (
													<button className={styles.viewIcon} onClick={() => handleViewDocument('back')} type="button">
														<Eye className={styles.eyeIcon} />
													</button>
												)}
												<div className={styles.previewIcon}>📄</div>
												<div className={styles.previewText}>
													{backDoc ? backDoc.name : sigDocument?.backDocName || 'Retro.file'}
												</div>
												<input
													type="file"
													id="backIdUpload"
													onChange={handleDocUpload('back')}
													accept="application/pdf,image/jpeg,image/jpg,image/png"
													style={{ display: 'none' }}
													disabled={!editing}
												/>
											</div>
										) : (
											<div className={styles.uploadContainer}>
												<div className={styles.uploadIcon}>📁</div>
												<span className={styles.uploadText}>{t('profile.documents.uploadPdf')}</span>
												<input
													type="file"
													id="backIdUpload"
													className={styles.uploadInput}
													onChange={handleDocUpload('back')}
													accept="application/pdf,image/jpeg,image/jpg,image/png"
													disabled={!editing}
												/>
											</div>
										)}
									</div>
								</div>
							</div>

							<div className={styles.buttonContainer}>
								<Button
									onClick={handleEditClick}
									typeSubmit="button"
									className={styles.btnSave}
									name={editing ? t('profile.cancelBtn') : t('profile.editBtn')}
									color="white"
									background="white"
								/>
								{editing && (
									<Button
										onClick={() => {}}
										typeSubmit="submit"
										className={styles.btnSave}
										name={t('profile.saveBtn')}
										color="black"
										background="white"
										disabled={updateProfile.loading || !editing}
									/>
								)}
							</div>
						</form>
					</div>

					{/* Colonna destra NOTIFICHE */}
					<div className={styles.rightContainer} id="notifications-section">
						<NotificationList />
					</div>
				</div>

				<button className={styles.privacyBtn} onClick={() => setShowPrivacyModal(true)}>
					{t('profile.seePrivacyPolicy')}
				</button>

				<button onClick={toggleDeleteAccount} className={styles.btnDelete}>
					{t('profile.deleteAccountBtn')}
				</button>
			</main>

			{/* Modale di conferma eliminazione account */}
			{deleteAccountPopup && (
				<div className={styles.deleteConfirmModal} onClick={(e) => e.stopPropagation()}>
					<div className={styles.deleteConfirmContent}>
						<p>{t('profile.deleteConfirmation')}</p>
						<div className={styles.deleteConfirmButtons}>
							<Button
								className={styles.choice_btn}
								typeSubmit="button"
								onClick={(e) => {
									if (e) handleConfirmDeleteAccount(e);
								}}
								name={t('common.yes')}
								color="white"
								background="white"
							/>
							<Button
								className={styles.choice_btn}
								typeSubmit="button"
								onClick={toggleDeleteAccount}
								name={t('common.no')}
								color="black"
								background="white"
							/>
						</div>
					</div>
				</div>
			)}

			{/* Password change popup */}
			<ConfirmationDialog
				isOpen={showPasswordPopup}
				title={t('profile.changePwdMessageTitle')}
				message={t('profile.changePwdMessage')}
				confirmText={t('popup.login')}
				cancelText={t('common.cancel')}
				onConfirm={handleChangePasswordRedirect}
				onCancel={handleClosePasswordPopup}
			/>

			<Modal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)}>
				<PrivacyPolicyCard displayLogo={false} />
			</Modal>

			<Modal isOpen={showDocumentModal} onClose={handleCloseDocumentModal}>
				{selectedDocument && (
					<DocumentViewer
						type={selectedDocument.type}
						data={selectedDocument.data}
						contentType={selectedDocument.contentType}
					/>
				)}
			</Modal>

			<Footer />
		</div>
	);
};

export default Profile;
